import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { css } from '@emotion/core'
import { DatePicker } from '@/components/organisms/customer/sales-manager/date-picker'
import { deleteStyle } from '@/constants/layout'
import { DiscountSettingType, EditDiscountType, PaymentSettingType, SalesDiscountType } from '@/models/sales'
import { ReservationType } from '@/models/reservation'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { Cascader, Select } from 'antd'
import { getRoomCascadeOptionsByReservations } from '@/utils/reservations'
import { initializeSelectBoxStyle } from '@/constants/form'
import { useTranslation } from 'react-i18next'
import { buildDiscountPaymentMethodOptions } from '@/libs/post-closing-sales-adjustment'
import { cascaderNameSearch } from '@/libs/cascader-name-search'

type NotConfirmedDiscountProps = {
  index: number
  discount: SalesDiscountType | EditDiscountType
  salesDiscounts: DiscountSettingType[]
  salesDiscountPayments: PaymentSettingType[]
  confirmedDay: string
  allowClosedDates?: boolean
  isEditMode: boolean
  changeNotConfirmedDate: (date, index) => void
  changeNotConfirmedState: (e, index) => void
  deleteDiscount: (index) => void
  setIsEditMode: (boolean) => void
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservations?: ReservationType[]
  reservation?: ReservationType
  advanceCompany?: any
  salesDiscountPaymentsGroupBySalesDate?: any
}

export const NotConfirmedDiscount: React.FC<NotConfirmedDiscountProps> = ({
  index,
  discount,
  salesDiscounts = [],
  salesDiscountPayments = [],
  confirmedDay,
  allowClosedDates = false,
  isEditMode,
  changeNotConfirmedDate,
  changeNotConfirmedState,
  deleteDiscount,
  setIsEditMode,
  accountsReceivableSettings,
  reservations = [],
  reservation,
  advanceCompany,
  salesDiscountPaymentsGroupBySalesDate,
}) => {
  const { t } = useTranslation()
  const [salesPaymentOptions, setSalesPaymentOptions] = useState<any[]>([])

  const displayRender = label => label[label.length - 1]

  const salesDiscountIds: string[] = salesDiscounts.map(d => d.id!)

  const roomsOptions = getRoomCascadeOptionsByReservations({ reservation, reservations })

  const updateDefaultIfNoPayments = _salesPaymentOptions => {
    const companyMasterIds = _salesPaymentOptions.flatMap(opt => opt.children?.map(c => c.value))
    const paymentMasterIds = _salesPaymentOptions.map(opt => opt.value)
    if (discount.companyId && companyMasterIds.includes(discount.companyId)) {
      return
    }
    if (paymentMasterIds.includes(discount.salesPaymentId)) {
      return
    }
    changeNotConfirmedState({ target: { value: _salesPaymentOptions[0].value, name: 'salesPaymentId' } }, index)
    changeNotConfirmedState({ target: { value: '', name: 'companyId' } }, index)
  }

  const computedPaymentMethodOptions = () => {
    const salesPaymentOptionsArr = buildDiscountPaymentMethodOptions(
      salesDiscountPayments,
      salesDiscountPaymentsGroupBySalesDate,
      discount.reservationId,
      discount.salesDate,
      advanceCompany,
    )

    if (salesPaymentOptionsArr.length && isEditMode) {
      updateDefaultIfNoPayments(salesPaymentOptionsArr)
    }
    setSalesPaymentOptions(salesPaymentOptionsArr)
  }

  useEffect(() => {
    computedPaymentMethodOptions()
  }, [discount.reservationId, discount.salesDate])

  useEffect(() => {
    computedPaymentMethodOptions()
  }, [salesDiscountPayments, salesDiscountPaymentsGroupBySalesDate, advanceCompany])

  return (
    <tr>
      <td>
        <div css={datePickerWrapperStyle} onClick={() => setIsEditMode(true)}>
          <DatePicker
            date={discount.salesDate ? moment(discount.salesDate) : null}
            setDate={async date => {
              changeNotConfirmedDate(date, index)
            }}
            confirmedDay={confirmedDay}
            allowClosedDates={allowClosedDates}
          />
        </div>
      </td>
      <td>
        <select
          name="salesDiscountId"
          css={isEditMode ? editModeLargeSelectBoxStyle : largeSelectBoxStyle}
          onClick={() => setIsEditMode(true)}
          defaultValue={discount.salesDiscountId}
          onChange={e => {
            changeNotConfirmedState(e, index)
          }}
        >
          {!discount.salesDiscountId && (
            <option selected={!discount.salesDiscountId} value="">
              {t('Please select')}
            </option>
          )}
          {/* Displayed only when discount item is deleted */}
          {discount.salesDiscountId && salesDiscountIds.indexOf(discount.salesDiscountId) === -1 && (
            <option selected value={discount.salesDiscountId}>
              {(discount as SalesDiscountType).salesDiscountName || ''}
            </option>
          )}
          {salesDiscounts?.map(salesDiscount => (
            <option key={salesDiscount.id} selected={discount.salesDiscountId === salesDiscount.id} value={salesDiscount.id}>
              {salesDiscount.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <Select
          onChange={value => {
            setIsEditMode(true)
            const reservationId = roomsOptions?.find(room => room.value === value)?.reservationId
            changeNotConfirmedState({ target: { value: reservationId, name: 'reservationId' } }, index)
            changeNotConfirmedState({ target: { value: value, name: 'guestRoomAssignId' } }, index)
          }}
          value={discount?.guestRoomAssignId || undefined}
          suffixIcon={isEditMode ? <img src={require('@/static/images/arrow_down_yellow.svg')} /> : <></>}
          placeholder={t('Please select')}
        >
          {roomsOptions.map(room => (
            <Select.Option key={room.value} value={room.value}>
              {room.label}
            </Select.Option>
          ))}
        </Select>
      </td>

      <td>
        <Cascader
          options={salesPaymentOptions}
          expandTrigger="hover"
          className={isEditMode ? 'cascader-customize' : 'cascader-customize not-edit-mode-cascader'}
          value={discount.companyId ? [discount.salesPaymentId, discount.companyId] : [discount.salesPaymentId]}
          displayRender={displayRender}
          popupClassName="popup-cascader-customize"
          showSearch={cascaderNameSearch}
          onChange={async value => {
            setIsEditMode(true)
            const eventChangeSalesPaymentId = {
              target: { value: value[0], name: 'salesPaymentId' },
            }

            const eventChangeCompanyId = {
              target: { value: value[1] ? value[1] : '', name: 'companyId' },
            }
            changeNotConfirmedState(eventChangeSalesPaymentId, index)
            changeNotConfirmedState(eventChangeCompanyId, index)
          }}
          allowClear={false}
          suffixIcon={<span></span>}
        />
      </td>

      <td>
        <input
          name="salesSubjectPrice"
          css={unitPriceStyle}
          onClick={() => setIsEditMode(true)}
          type="text"
          value={discount.salesSubjectPrice}
          onChange={e => changeNotConfirmedState(e, index)}
          placeholder="0000"
          className="unitPriceStyle"
        />
      </td>
      <td>
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={true}
          suffixIcon={isEditMode ? <img src={require('@/static/images/arrow_down_yellow.svg')} /> : <></>}
          onChange={value => {
            setIsEditMode(true)
            changeNotConfirmedState(
              {
                target: { value, name: 'salesAccountsReceivableMasterId' },
              },
              index,
            )
          }}
          value={discount.salesAccountsReceivableMasterId || ''}
        >
          <Select.Option value="">-</Select.Option>
          {accountsReceivableSettings.map((accountsReceivable, index) => (
            <Select.Option key={index} value={accountsReceivable.id}>
              {accountsReceivable.name}
            </Select.Option>
          ))}
          {discount.salesAccountsReceivableMasterId &&
            !accountsReceivableSettings.some(accountsReceivable => accountsReceivable.id === discount.salesAccountsReceivableMasterId) && (
              <Select.Option value={discount.salesAccountsReceivableMasterId || ''}>
                {(discount as SalesDiscountType).salesAccountsReceivableName || ''}
              </Select.Option>
            )}
        </Select>
      </td>
      <td>
        {isEditMode && (
          <div css={deleteStyle} className="delete-none-0">
            <img onClick={() => deleteDiscount(index)} src={require('@/static/images/delete_yellow.svg')} />
          </div>
        )}
      </td>
    </tr>
  )
}

const datePickerWrapperStyle = css({
  width: 80,
})

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
})

const middleInputStyle = css(selectBoxStyle, {
  marginRight: '2.5%',
  width: '8.5%',
  backgroundImage: 'none',
  '::placeholder': {
    color: '#ccc',
  },
})

export const unitPriceStyle = css(middleInputStyle, {
  marginLeft: 12,
})

export const largeSelectBoxStyle = css(selectBoxStyle, {
  marginRight: '1.5%',
  width: '90%',
  backgroundPosition: '95% center',
})

export const xlargeSelectBoxStyle = css(selectBoxStyle, {
  width: 'calc(20%)',
  margin: '0 2% 0 1.5%',
  backgroundPosition: '95% center',
})

const editModoSelectBoxStyle = css(selectBoxStyle, {
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
})

export const editModeLargeSelectBoxStyle = css(editModoSelectBoxStyle, largeSelectBoxStyle)
